import React, { useEffect, useRef, useState, useCallback } from "react";
import { Container, Row, Col, Button, Card } from "react-bootstrap";
import { getPeopleForUser } from "../../apeople";
import { AddPersonInline } from "./AddPersonInline";
import { EditPersonInline } from "./EditPersonInline";

export function ViewPeople({ refresh, onPeopleUpdate = () => {}, addTrigger }) {
  const [people, setPeople] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingPersonId, setEditingPersonId] = useState(null);

  const addRef = useRef(null);
  const firstTriggerSeen = useRef(true);
  const lastTriggerRef = useRef(addTrigger);

  const reload = useCallback(async () => {
    const token = sessionStorage.getItem("user");
    if (!token) {
      setPeople([]);
      return;
    }

    try {
      const data = await getPeopleForUser(token);
      setPeople(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("❌ Failed to load people:", err);
      setPeople([]);
    }
  }, []);

  // helper:
  const PEOPLE_TYPE_LABELS = {
    GP: "General Practitioner (GP)",
    Cardio: "Cardiologist",
    Derm: "Dermatologist",
    Endo: "Endocrinologist",
    Gastro: "Gastroenterologist",
    Neuro: "Neurologist",
    Onco: "Oncologist",
    Ortho: "Orthopaedic Surgeon",
    Pead: "Paediatrician",
    Phys: "Physician",
    Psych: "Psychiatrist",
    Pulmo: "Pulmonologist",

    ENT: "ENT Specialist",
    Eye: "Ophthalmologist",
    Radio: "Radiologist",
    Patho: "Pathologist",

    Physio: "Physiotherapist",
    OT: "Occupational Therapist",
    Biokineticist: "Biokineticist",
    Dietician: "Dietitian",
    Speech: "Speech Therapist",
    Audio: "Audiologist",
    Psychologist: "Clinical Psychologist",
    Chiro: "Chiropractor",
    Podiatrist: "Podiatrist",

    Nurse: "Nurse",
    Pharma: "Pharmacist",
    Dentist: "Dentist",

    Other: "Other",
  };

  useEffect(() => {
    reload();
  }, [reload, refresh]);

  // Only open Add when header button is clicked (skip first render)
  useEffect(() => {
    if (firstTriggerSeen.current) {
      firstTriggerSeen.current = false;
      return;
    }
    if (lastTriggerRef.current !== addTrigger) {
      setShowAdd(true);
      setEditingPersonId(null);
      lastTriggerRef.current = addTrigger;

      setTimeout(() => {
        addRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    }
  }, [addTrigger]);

  const handleEditClick = (personId) => {
    setShowAdd(false);
    setEditingPersonId(personId);
  };

  const exitEditMode = () => setEditingPersonId(null);

  const handleAdded = async () => {
    await reload();
    onPeopleUpdate();
    setShowAdd(false);
  };

  const handleEdited = async () => {
    await reload();
    onPeopleUpdate();
    setEditingPersonId(null);
  };

  return (
    <Container fluid className="bg-transparent">
      <div ref={addRef} />

      {/* Inline Add panel (same look as Services) */}
      {showAdd && (
        <Card className="mb-3 shadow-sm">
          <Card.Header
            className="d-flex justify-content-between align-items-center"
            style={{ background: "#CFE6FF" }}
          >
            <h6 className="mb-0 fw-bold text-primary">Add Person</h6>
            <Button
              variant="outline-secondary"
              size="sm"
              type="button"
              onClick={() => setShowAdd(false)}
            >
              Close
            </Button>
          </Card.Header>
          <Card.Body>
            <AddPersonInline
              onPeopleUpdate={handleAdded}
              onClose={() => setShowAdd(false)}
            />
          </Card.Body>
        </Card>
      )}

      {(people ?? [])
        .slice()
        .sort((a, b) => {
          const aName = `${a?.lastName ?? ""} ${
            a?.firstName ?? ""
          }`.toLowerCase();
          const bName = `${b?.lastName ?? ""} ${
            b?.firstName ?? ""
          }`.toLowerCase();
          return aName.localeCompare(bName);
        })
        .map((p) => {
          const id = typeof p?._id === "string" ? p._id : p?._id?.$oid || p?.id;

          const isEditing = editingPersonId === id;

          return (
            <Row key={id} className="gy-3 mb-3">
              <Col md={12}>
                <Card className="shadow-sm">
                  <Card.Header
                    className="d-flex justify-content-between align-items-center"
                    style={{ background: "#CFE6FF" }}
                  >
                    <div className="fw-bold text-primary">
                      {p?.firstName || p?.lastName
                        ? `${p?.title} ${p?.firstName ?? ""} ${
                            p?.lastName ?? ""
                          }`.trim()
                        : "Unnamed Person"}
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      {isEditing ? (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          type="button"
                          onClick={exitEditMode}
                        >
                          Close
                        </Button>
                      ) : (
                        <Button
                          variant=""
                          type="button"
                          onClick={() => handleEditClick(id)}
                          aria-label="Edit"
                          title="Edit"
                        >
                          <i className="fas fa-edit text-primary" />
                        </Button>
                      )}
                    </div>
                  </Card.Header>

                  <Card.Body className={isEditing ? "p-0" : undefined}>
                    {isEditing ? (
                      <div style={{ padding: "1rem" }}>
                        <EditPersonInline
                          personId={id}
                          initialValue={p}
                          onHide={exitEditMode}
                          onPeopleUpdate={handleEdited}
                        />
                      </div>
                    ) : (
                      <Row className="g-2 align-items-center">
                        <Col md={4} lg={4}>
                          <div className="d-flex">
                            <strong style={{ minWidth: 70 }}>Type:</strong>
                            <span className="ms-2">
                              {PEOPLE_TYPE_LABELS[p?.type] || p?.type || "-"}
                            </span>
                          </div>
                        </Col>

                        <Col md={2} lg={2}>
                          <div className="d-flex">
                            <strong style={{ minWidth: 70 }}>BHF #:</strong>
                            <span className="ms-2">{p?.bhfNumber ?? "-"}</span>
                          </div>
                        </Col>

                        <Col md={3} lg={3}>
                          <div className="d-flex">
                            <strong style={{ minWidth: 80 }}>HPCSA #:</strong>
                            <span className="ms-2">
                              {p?.hpcsaNumber ?? "-"}
                            </span>
                          </div>
                        </Col>
                      </Row>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          );
        })}
    </Container>
  );
}
